import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Factory as ProductIcon,
  Groups as ICPIcon,
  Save as SaveIcon,
} from "@mui/icons-material";
import {
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  InputAdornment,
  Stack,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import React, { useState } from "react";

import type { ICP, Product } from "../../types/accountIntelligence";
import { MOCK_ACCOUNT_STREAM_MATCHES, MOCK_ICPS, MOCK_PRODUCTS, MOCK_STREAMS } from "./mockData";

// ─── Chip input ────────────────────────────────────────────────────────────────

const ChipInput: React.FC<{
  label: string;
  values: string[];
  onChange: (v: string[]) => void;
}> = ({ label, values, onChange }) => {
  const [input, setInput] = useState("");

  const add = (): void => {
    const t = input.trim();
    if (t && !values.includes(t)) { onChange([...values, t]); setInput(""); }
  };

  return (
    <Box>
      <TextField
        fullWidth
        size="small"
        label={label}
        value={input}
        onChange={(e) => setInput(e.target.value)}
        onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(); } }}
        onBlur={add}
        placeholder="Type and press Enter"
        InputProps={{
          endAdornment: input ? (
            <InputAdornment position="end">
              <Button size="small" onClick={add} sx={{ minWidth: 0, px: 1 }}>Add</Button>
            </InputAdornment>
          ) : null,
        }}
      />
      {values.length > 0 && (
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75, mt: 0.75 }}>
          {values.map((v) => (
            <Chip key={v} label={v} size="small" onDelete={() => onChange(values.filter((x) => x !== v))} />
          ))}
        </Box>
      )}
    </Box>
  );
};

// ─── Product card + edit dialog ───────────────────────────────────────────────

const ProductCard: React.FC<{ product: Product }> = ({ product: initial }) => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [p, setP] = useState<Product>(initial);
  const [draft, setDraft] = useState<Product>(initial);

  const openDialog = (): void => { setDraft(p); setDialogOpen(true); };
  const save = (): void => { setP(draft); setDialogOpen(false); };
  const cancel = (): void => setDialogOpen(false);

  return (
    <>
      <Card
        elevation={0}
        sx={{
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 1.5,
          width: "100%",
          height: "100%",
          display: "flex",
          flexDirection: "column",
          "&:hover": { borderColor: "primary.main" },
          transition: "border-color 0.15s",
        }}
      >
        {/* Card icon + title */}
        <CardContent sx={{ flex: 1, pb: 1 }}>
          <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", mb: 1.5 }}>
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: 1.5,
                backgroundColor: "rgba(25,118,210,0.12)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <ProductIcon sx={{ fontSize: 22, color: "primary.main" }} />
            </Box>
            <Tooltip title="Delete (disabled in mockup)">
              <span>
                <IconButton size="small" disabled sx={{ color: "text.disabled", mt: -0.5, mr: -0.5 }}>
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          </Box>

          <Typography variant="subtitle1" fontWeight={700} mb={0.5} lineHeight={1.3}>
            {p.name}
          </Typography>
          <Typography
            variant="body2"
            color="text.secondary"
            mb={1.5}
            sx={{
              display: "-webkit-box",
              WebkitLineClamp: 3,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
              lineHeight: 1.5,
            }}
          >
            {p.description}
          </Typography>

          {p.capabilityTags.length > 0 && (
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mb: 1.5 }}>
              {p.capabilityTags.map((t) => (
                <Chip key={t} label={t} size="small" sx={{ fontSize: "0.68rem" }} />
              ))}
            </Box>
          )}

          {p.valueProposition && (
            <Box
              sx={{
                borderLeft: "2px solid",
                borderColor: "primary.main",
                pl: 1.25,
                py: 0.25,
              }}
            >
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{
                  display: "-webkit-box",
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: "vertical",
                  overflow: "hidden",
                }}
              >
                {p.valueProposition}
              </Typography>
            </Box>
          )}
        </CardContent>

        <Divider />
        <CardActions sx={{ px: 2, py: 1 }}>
          <Button
            size="small"
            startIcon={<EditIcon fontSize="inherit" />}
            onClick={openDialog}
            sx={{ textTransform: "none", fontSize: "0.75rem" }}
          >
            Edit
          </Button>
        </CardActions>
      </Card>

      {/* Edit dialog */}
      <Dialog open={dialogOpen} onClose={cancel} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Edit Product / Service</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <TextField fullWidth size="small" label="Name" value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} />
            <TextField fullWidth multiline rows={3} size="small" label="Description" value={draft.description} onChange={(e) => setDraft({ ...draft, description: e.target.value })} />
            <TextField fullWidth multiline rows={2} size="small" label="Value Proposition" value={draft.valueProposition} onChange={(e) => setDraft({ ...draft, valueProposition: e.target.value })} />
            <ChipInput label="Capability Tags" values={draft.capabilityTags} onChange={(v) => setDraft({ ...draft, capabilityTags: v })} />
            <ChipInput label="Relevant Systems / Processes" values={draft.relevantSystems} onChange={(v) => setDraft({ ...draft, relevantSystems: v })} />
            <TextField fullWidth size="small" label="Exclusions" value={draft.exclusions} onChange={(e) => setDraft({ ...draft, exclusions: e.target.value })} />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={cancel} sx={{ textTransform: "none" }}>Cancel</Button>
          <Button variant="contained" startIcon={<SaveIcon fontSize="inherit" />} onClick={save} sx={{ textTransform: "none" }}>
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

// ─── ICP card + edit dialog ───────────────────────────────────────────────────

const ICPCard: React.FC<{ icp: ICP }> = ({ icp: initial }) => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [icp, setIcp] = useState<ICP>(initial);
  const [draft, setDraft] = useState<ICP>(initial);

  const streamIds = MOCK_STREAMS.filter((s) => s.icp.id === icp.id).map((s) => s.id);
  const accountCount = MOCK_ACCOUNT_STREAM_MATCHES.filter((m) => streamIds.includes(m.streamId)).length;
  const watchingCount = MOCK_ACCOUNT_STREAM_MATCHES.filter(
    (m) => streamIds.includes(m.streamId) && (m.status === "watching" || m.status === "engaged"),
  ).length;

  const openDialog = (): void => { setDraft(icp); setDialogOpen(true); };
  const save = (): void => { setIcp(draft); setDialogOpen(false); };
  const cancel = (): void => setDialogOpen(false);

  return (
    <>
      <Card
        elevation={0}
        sx={{
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 1.5,
          width: "100%",
          height: "100%",
          display: "flex",
          flexDirection: "column",
          "&:hover": { borderColor: "primary.main" },
          transition: "border-color 0.15s",
        }}
      >
        <CardContent sx={{ flex: 1, pb: 1 }}>
          {/* Icon + delete */}
          <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", mb: 1.5 }}>
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: 1.5,
                backgroundColor: "rgba(46,125,50,0.12)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <ICPIcon sx={{ fontSize: 22, color: "#2e7d32" }} />
            </Box>
            <Tooltip title="Delete (disabled in mockup)">
              <span>
                <IconButton size="small" disabled sx={{ color: "text.disabled", mt: -0.5, mr: -0.5 }}>
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          </Box>

          {/* Name + account stats */}
          <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 1, mb: 0.5 }}>
            <Typography variant="subtitle1" fontWeight={700} lineHeight={1.3} sx={{ flex: 1 }}>
              {icp.name}
            </Typography>
          </Box>

          {/* Account count badges */}
          <Box sx={{ display: "flex", gap: 0.75, mb: 1.5, flexWrap: "wrap" }}>
            <Chip
              label={`${accountCount} accounts`}
              size="small"
              sx={{ fontSize: "0.68rem", color: "primary.main", backgroundColor: "rgba(25,118,210,0.1)" }}
            />
            <Chip
              label={`${watchingCount} monitoring`}
              size="small"
              sx={{ fontSize: "0.68rem", color: "#2e7d32", backgroundColor: "rgba(46,125,50,0.1)" }}
            />
          </Box>

          {/* Account types + size */}
          <Typography variant="caption" color="text.secondary" display="block" mb={0.25}>
            {icp.accountTypes.join(" · ")}
          </Typography>
          <Typography variant="caption" color="text.disabled" display="block" mb={1.25}>
            {icp.companySize}
          </Typography>

          {/* Geography chips */}
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mb: 1.5 }}>
            {icp.geography.map((g) => (
              <Chip key={g} label={g} size="small" variant="outlined" sx={{ fontSize: "0.68rem" }} />
            ))}
          </Box>

          {/* Industries */}
          {icp.industries.length > 0 && (
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
              {icp.industries.map((ind) => (
                <Chip key={ind} label={ind} size="small" sx={{ fontSize: "0.68rem", backgroundColor: "rgba(255,255,255,0.05)" }} />
              ))}
            </Box>
          )}
        </CardContent>

        <Divider />
        <CardActions sx={{ px: 2, py: 1 }}>
          <Button
            size="small"
            startIcon={<EditIcon fontSize="inherit" />}
            onClick={openDialog}
            sx={{ textTransform: "none", fontSize: "0.75rem" }}
          >
            Edit
          </Button>
        </CardActions>
      </Card>

      {/* Edit dialog */}
      <Dialog open={dialogOpen} onClose={cancel} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Edit Ideal Customer Profile</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <TextField fullWidth size="small" label="ICP Name" value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} />
            <ChipInput label="Account Types" values={draft.accountTypes} onChange={(v) => setDraft({ ...draft, accountTypes: v })} />
            <ChipInput label="Industries / Domains" values={draft.industries} onChange={(v) => setDraft({ ...draft, industries: v })} />
            <ChipInput label="Geography" values={draft.geography} onChange={(v) => setDraft({ ...draft, geography: v })} />
            <TextField fullWidth size="small" label="Company Size" value={draft.companySize} onChange={(e) => setDraft({ ...draft, companySize: e.target.value })} />
            <TextField fullWidth multiline rows={2} size="small" label="Buying Context" value={draft.buyingContext} onChange={(e) => setDraft({ ...draft, buyingContext: e.target.value })} />
            <TextField fullWidth size="small" label="Exclusions" value={draft.exclusions} onChange={(e) => setDraft({ ...draft, exclusions: e.target.value })} />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={cancel} sx={{ textTransform: "none" }}>Cancel</Button>
          <Button variant="contained" startIcon={<SaveIcon fontSize="inherit" />} onClick={save} sx={{ textTransform: "none" }}>
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

// ─── Add card ──────────────────────────────────────────────────────────────────

const AddCard: React.FC<{ label: string }> = ({ label }) => (
  <Card
    elevation={0}
    sx={{
      border: "1px dashed",
      borderColor: "divider",
      borderRadius: 1.5,
      width: "100%",
      height: "100%",
      minHeight: 180,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      cursor: "pointer",
      flexDirection: "column",
      gap: 1,
      color: "text.disabled",
      "&:hover": { borderColor: "primary.main", color: "primary.main", backgroundColor: "rgba(25,118,210,0.04)" },
      transition: "all 0.15s",
    }}
  >
    <AddIcon sx={{ fontSize: 28 }} />
    <Typography variant="body2" fontWeight={600}>
      {label}
    </Typography>
  </Card>
);

// ─── Main Configuration ────────────────────────────────────────────────────────

type ConfigTab = "products" | "icps";

const Configuration: React.FC = () => {
  const [activeTab, setActiveTab] = useState<ConfigTab>("products");

  return (
    <Box sx={{ height: "100%", display: "flex", flexDirection: "column", overflow: "hidden" }}>
      {/* Header */}
      <Box sx={{ px: 3, pt: 2.5, pb: 0, flexShrink: 0 }}>
        <Typography variant="h5" fontWeight={700}>Configuration</Typography>
        <Typography variant="body2" color="text.secondary" mt={0.25}>
          Manage your products, services, and ideal customer profiles
        </Typography>
      </Box>

      {/* Tabs */}
      <Box sx={{ px: 3, borderBottom: 1, borderColor: "divider", flexShrink: 0, mt: 2 }}>
        <Tabs
          value={activeTab}
          onChange={(_, v: ConfigTab) => setActiveTab(v)}
          sx={{ minHeight: 40 }}
          TabIndicatorProps={{ style: { height: 2 } }}
        >
          <Tab value="products" label={`Products & Services (${MOCK_PRODUCTS.length})`} sx={{ textTransform: "none", minHeight: 40, py: 0 }} />
          <Tab value="icps" label={`Ideal Customer Profiles (${MOCK_ICPS.length})`} sx={{ textTransform: "none", minHeight: 40, py: 0 }} />
        </Tabs>
      </Box>

      {/* Content */}
      <Box sx={{ flex: 1, overflowY: "auto", p: 3 }}>
        {activeTab === "products" && (
          <Grid container spacing={2} alignItems="stretch">
            {MOCK_PRODUCTS.map((p) => (
              <Grid item xs={12} sm={6} lg={4} key={p.id} sx={{ display: "flex", flexDirection: "column" }}>
                <ProductCard product={p} />
              </Grid>
            ))}
            <Grid item xs={12} sm={6} lg={4} sx={{ display: "flex", flexDirection: "column" }}>
              <AddCard label="Add Product" />
            </Grid>
          </Grid>
        )}

        {activeTab === "icps" && (
          <Grid container spacing={2} alignItems="stretch">
            {MOCK_ICPS.map((icp) => (
              <Grid item xs={12} sm={6} lg={4} key={icp.id} sx={{ display: "flex", flexDirection: "column" }}>
                <ICPCard icp={icp} />
              </Grid>
            ))}
            <Grid item xs={12} sm={6} lg={4} sx={{ display: "flex", flexDirection: "column" }}>
              <AddCard label="Add ICP" />
            </Grid>
          </Grid>
        )}
      </Box>
    </Box>
  );
};

export default Configuration;
